import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles, Button, Text, Subtitle1, tokens, Card, Avatar } from "@fluentui/react-components";
import { Sidebar } from "../sidebar/Sidebar";
import { useEvent } from "./EventContextProvider";
import { FormattedMessage } from "react-intl";
import { ReactComponent as Virtual } from "../icons/Virtual.svg";
import { ReactComponent as InPerson } from "../icons/InPerson.svg";
import { ReactComponent as Hybrid } from "../icons/Hybrid.svg";
import {
  enterScheduledSession,
  eventHasConcluded,
  eventScheduleDayKey,
  formatEventScheduleDay,
  formatEventScheduleTime,
  getEndTime
} from "../../utils/events-utils";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "20px"
  },
  card: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px"
  },
  time: {
    color: "var(--Gray-3, #828282)"
  },
  title: {
    margin: 0
  },
  capacity: {
    color: "var(--Gray-3, #828282)"
  },
  soldOut: {
    color: tokens.colorPaletteRedForeground1
  },
  reserved: {
    color: tokens.colorPaletteGreenForeground3
  },
  description: {
    color: "var(--neutralForeground2)"
  },
  footerRow: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row-reverse",
    alignItems: "center",
    marginTop: "5px"
  },
  presenterName: {
    marginLeft: "8px"
  },
  typeBadge: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "500",
    color: "var(--Gray-3, #828282)",
    "& svg": {
      fill: `${tokens.colorNeutralForeground3}!important`,
      color: `${tokens.colorNeutralForeground3}!important`
    }
  },
  typeAndCapacity: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  }
});

const getPresentationType = presentation => {
  const type = presentation?.type?.toLowerCase();
  if (type === "in_person" || type === "in-person") return "in_person";
  if (type === "hybrid") return "hybrid";
  return "virtual";
};

const TypeBadge = ({ type, classes }) => {
  const config = {
    virtual: {
      icon: <Virtual />,
      label: "Virtual",
      className: classes.virtualBadge
    },
    in_person: {
      icon: <InPerson />,
      label: "In Person",
      className: classes.inPersonBadge
    },
    hybrid: {
      icon: <Hybrid />,
      label: "Hybrid",
      className: classes.hybridBadge
    }
  };

  const badgeConfig = config[type] || config.virtual;

  return (
    <div className={`${classes.typeBadge} ${badgeConfig.className}`}>
      {badgeConfig.icon}
      <span>{badgeConfig.label}</span>
    </div>
  );
};

TypeBadge.propTypes = {
  type: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired
};

export const ScheduleSidebar = ({ onClose }) => {
  const classes = useStyles();
  const { eventData, handleRSVP, handleAdmission, handleConcludedSessionVisit } = useEvent();

  const allPresentations = useMemo(() => {
    if (!eventData) return [];
    // Try to normalize to a flat array
    const list = Array.isArray(eventData.presentations) ? eventData.presentations : [];
    return list
      .filter(p => p?.start_time && p?.end_time)
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  }, [eventData]);

  const days = useMemo(() => {
    const keys = Array.from(
      new Set(allPresentations.map(p => eventScheduleDayKey(p.start_time, p.timezone || eventData?.timezone)))
    );
    return keys.sort();
  }, [allPresentations, eventData?.timezone]);

  const [currentTab, setCurrentTab] = useState(days[0] ?? "");

  const tabs = useMemo(
    () =>
      days.map(key => {
        const presentation = allPresentations.find(
          item => eventScheduleDayKey(item.start_time, item.timezone || eventData?.timezone) === key
        );
        return {
          value: key,
          label: presentation
            ? formatEventScheduleDay(presentation.start_time, presentation.timezone || eventData?.timezone)
            : key
        };
      }),
    [allPresentations, days, eventData?.timezone]
  );

  const listForDay = useMemo(
    () =>
      allPresentations.filter(p => eventScheduleDayKey(p.start_time, p.timezone || eventData?.timezone) === currentTab),
    [allPresentations, currentTab, eventData?.timezone]
  );

  const handleEnter = (presentation, hasConcluded) =>
    enterScheduledSession({
      sessionSlug: presentation.slug,
      hasConcluded,
      visitConcluded: handleConcludedSessionVisit,
      admit: handleAdmission
    });

  return (
    <Sidebar
      title={
        <Subtitle1>
          <FormattedMessage id="schedule-sidebar.title" defaultMessage="Schedule" />
        </Subtitle1>
      }
      onClose={onClose}
      tabs={tabs}
      currentTab={currentTab}
      changeTab={setCurrentTab}
      position="end"
    >
      <div className={classes.list}>
        {listForDay.map(p => {
          const totalCapacity = p.capacity_total ?? p.capacity ?? p.capacity_max ?? 50;
          const usedCapacity = p.capacity_used ?? p.used_capacity ?? p.attendees_count ?? 0;
          const soldOut = usedCapacity >= totalCapacity;
          const isReserved = !!p.is_attending;
          const presentationEndTime = getEndTime(p);
          const hasConcluded = eventHasConcluded(presentationEndTime);
          const isInPerson = p.type !== "virtual" && p.address?.id;
          const entryAvailable = p.entry_available === true;
          const entryLabel =
            p.entry_kind === "external"
              ? hasConcluded
                ? "Visit Presentation"
                : "Join Presentation"
              : hasConcluded
                ? "Explore verse"
                : "Join Verse";
          const showEnterButton = (isReserved || hasConcluded) && !isInPerson && entryAvailable;
          const showConcludedButton = hasConcluded && (isInPerson || !entryAvailable);

          return (
            <Card key={p.slug || `${p.name}-${p.start_time}`} className={classes.card}>
              <div className={classes.row}>
                <Text className={classes.time}>
                  {formatEventScheduleTime(p.start_time_next || p.start_time, p.timezone || eventData?.timezone)} -{" "}
                  {formatEventScheduleTime(p.end_time_next || p.end_time, p.timezone || eventData?.timezone)}
                </Text>
                <div className={classes.typeAndCapacity}>
                  <TypeBadge type={getPresentationType(p)} classes={classes} />
                  <Text className={classes.capacity}>
                    <FormattedMessage id="schedule-sidebar.capacity" defaultMessage="Capacity" />: {usedCapacity}/
                    {totalCapacity}
                  </Text>
                </div>
              </div>
              <h3 className={classes.title}>{p.title || p.name}</h3>
              {p.presenters?.length > 0 &&
                p.presenters.map(x => (
                  <div key={x.name}>
                    <Avatar name={x.name} color="colorful" image={x.image || undefined} />
                    <Text className={classes.presenterName}>{x.name}</Text>
                  </div>
                ))}
              {p.description && <Text className={classes.description}>{p.description}</Text>}
              {/* Only show the footer row if the presentation has not concluded */}
              <div className={classes.footerRow}>
                {!hasConcluded && (
                  <>
                    {isReserved ? (
                      <Text className={classes.reserved} weight="semibold">
                        <FormattedMessage id="schedule-sidebar.reserved" defaultMessage="Reserved" />
                      </Text>
                    ) : soldOut ? (
                      <Text className={classes.soldOut} weight="semibold">
                        <FormattedMessage id="schedule-sidebar.sold-out" defaultMessage="Sold Out" />
                      </Text>
                    ) : (
                      <Button appearance="outline" className={classes.reserveButton} onClick={() => handleRSVP(p.slug)}>
                        <FormattedMessage id="schedule-sidebar.reserve-button" defaultMessage="Reserve a Spot" />
                      </Button>
                    )}
                  </>
                )}
                <div>
                  <>
                    {showEnterButton && (
                      <Button
                        appearance="outline"
                        className={classes.enterButton}
                        onClick={() => handleEnter(p, hasConcluded)}
                      >
                        {entryLabel}
                      </Button>
                    )}
                  </>
                  <>
                    {showConcludedButton && (
                      <Button appearance="outline" className={classes.concludedButton}>
                        <FormattedMessage id="schedule-sidebar.explore-verse" defaultMessage="Concluded" />
                      </Button>
                    )}
                  </>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </Sidebar>
  );
};

ScheduleSidebar.propTypes = {
  onClose: PropTypes.func
};

export default ScheduleSidebar;
